
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { UserRole } from '@/types';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  profile: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    console.log('AuthProvider: Setting up auth state listener');
    
    // Set up listener for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state changed:', event, currentSession?.user?.id);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          // Only fetch profile, don't auto-navigate
          setTimeout(() => {
            fetchProfile(currentSession.user.id);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          console.log('SIGNED_OUT event received, clearing profile');
          setProfile(null);
          // Clear any cached data and ensure clean state
          setLoading(false);
        } else if (!currentSession?.user) {
          // No user session, set loading to false
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log('AuthProvider: Initial session check', currentSession?.user?.id);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => {
      console.log('AuthProvider: Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, [navigate]);

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        setLoading(false); // Set loading to false even on error
      } else if (data) {
        console.log('Profile fetched successfully:', data);
        setProfile(data);
        setLoading(false); // Set loading to false after successful fetch
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false); // Set loading to false on catch
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        toast({
          title: 'Login failed',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }

      if (data.user) {
        toast({
          title: 'Login successful',
          description: `Welcome back!`,
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('Starting logout process...');
      
      // Step 1: Sign out from Supabase first
      console.log('Calling supabase.auth.signOut()...');
      const { error } = await supabase.auth.signOut({
        scope: 'local' // Only sign out locally, not globally
      });
      
      if (error) {
        console.error('Supabase logout error:', error);
        // Continue with cleanup even if there's an error
      } else {
        console.log('Supabase logout successful');
      }
      
      // Step 2: Clear local state
      console.log('Clearing local state...');
      setProfile(null);
      setUser(null);
      setSession(null);
      
      // Step 3: Show success message and navigate
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
      
      // Step 4: Force navigation
      console.log('Navigating to login page...');
      navigate('/login', { replace: true });
      
    } catch (error) {
      console.error('Logout error:', error);
      
      // Always clear state and navigate even on error
      setProfile(null);
      setUser(null);
      setSession(null);
      
      toast({
        title: 'Logged out',
        description: 'You have been logged out.',
      });
      
      navigate('/login', { replace: true });
    }
  };

  const contextValue = {
    session,
    user,
    profile,
    loading,
    signIn,
    signOut
  };

  console.log('AuthProvider: Rendering with context value', { user: user?.id, profile: profile?.id, loading });

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth must be used within an AuthProvider');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
